/**
 * React Query hooks for Families
 * Provides cached, optimized access to family records
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getAllFamilies,
  getFamilyById,
  createFamily,
  updateFamily,
  deleteFamily,
  addFamilyMember,
  removeFamilyMember,
  Family,
  FamilyFilters,
} from '../services/familyService';

/**
 * Hook to fetch families with optional search and server-side pagination
 * Automatically caches and deduplicates requests
 */
export function useFamilies(filters?: FamilyFilters & { enabled?: boolean }) {
  const { enabled, ...queryFilters } = filters || {};
  const normalizedFilters: Partial<FamilyFilters> = Object.fromEntries(
    Object.entries(queryFilters).filter(([, value]) => value !== undefined)
  );

  return useQuery({
    queryKey: ['families', normalizedFilters],
    queryFn: () => getAllFamilies(queryFilters),
    enabled: enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab (background refetching)
    // Keep prior results on screen while the next page/search query loads,
    // matching the fix applied to useIndividuals.
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch a single family by ID
 */
export function useFamily(familyID: number | string | undefined) {
  return useQuery({
    queryKey: ['family', familyID],
    queryFn: () => getFamilyById(Number(familyID!)),
    enabled: !!familyID,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new family
 * Uses optimistic updates for better perceived performance
 */
export function useCreateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFamily,
    onMutate: async (newFamily) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['families'] });

      // Snapshot previous value
      const previousFamilies = queryClient.getQueriesData({ queryKey: ['families'] });

      // Optimistically add to cache
      queryClient.setQueriesData({ queryKey: ['families'] }, (old: any) => {
        if (!old?.data) {return old;}
        return {
          ...old,
          data: [...old.data, newFamily as Family],
          pagination: {
            ...old.pagination,
            totalItems: old.pagination.totalItems + 1,
          },
        };
      });

      return { previousFamilies };
    },
    onError: (_err, _newFamily, context) => {
      // Rollback on error
      if (context?.previousFamilies) {
        context.previousFamilies.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['families'] });
    },
  });
}

/**
 * Hook to update a family
 */
export function useUpdateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyID, data }: { familyID: number; data: any }) =>
      updateFamily(familyID, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['family', variables.familyID] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
    },
  });
}

/**
 * Hook to delete a family
 */
export function useDeleteFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFamily,
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: ['family', deletedId] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
    },
  });
}

/**
 * Hook to add a member to a family
 * Uses optimistic updates for better perceived performance
 */
export function useAddFamilyMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyID, member }: { familyID: number; member: any }) =>
      addFamilyMember(familyID, member),
    onMutate: async ({ familyID }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['family', familyID] });
      
      // Snapshot previous value
      const previousFamily = queryClient.getQueryData<Family>(['family', familyID]);
      
      return { previousFamily };
    },
    onSuccess: (_, variables) => {
      // Invalidate to fetch fresh data
      queryClient.invalidateQueries({ queryKey: ['family', variables.familyID] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
    },
  });
}

/**
 * Hook to remove a member from a family
 */
export function useRemoveFamilyMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyID, memberID }: { familyID: number; memberID: number }) =>
      removeFamilyMember(familyID, memberID),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['family', variables.familyID] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
    },
  });
}

